import React from 'react';
import SiteFooter from '../components/SiteFooter';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Loader2, AlertCircle, Home, MessageCircle, Truck, Store, ClipboardList } from 'lucide-react';

/**
 * KPay 收銀台回轉頁（returnUrl 落點 = 訂單完成頁）
 * 支付完成後 KPay 將用戶瀏覽器跳轉回此頁：
 *   1. 顯示「正在確認付款結果」並輪詢 /api/public/orders/:orderNum/kpay/status（5 秒 × 24 次）
 *   2. 狀態端點同時擔任漏單補救（KPay 已收款但通知未達時即場補確認）
 *   3. 確認後顯示完整訂單摘要（實付金額、回贈券扣減、送貨資訊、品項明細）
 */
interface OrderSummary {
  orderNum: string;
  payable: number;
  voucherDiscount: number;
  deliveryFee: number;
  subtotal: number;
  deliveryDate: string | null;
  deliveryTime: string | null;
  estate: string | null;
  orderType: string;
  items: Array<{ name: string; quantity: number; unitPrice: number }>;
}

const KpayReturn: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderNum = searchParams.get('orderNum') || '';

  const [status, setStatus] = React.useState<'polling' | 'paid' | 'timeout' | 'error'>('polling');
  const [pollCount, setPollCount] = React.useState(0);
  const [summary, setSummary] = React.useState<OrderSummary | null>(null);

  React.useEffect(() => {
    if (!orderNum) {
      setStatus('error');
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetch(`/api/public/orders/${encodeURIComponent(orderNum)}/kpay/status`);
        const data = res.ok ? await res.json().catch(() => null) : null;
        if (cancelled) return;
        if (data?.paymentConfirmed) {
          if (data.order) setSummary(data.order as OrderSummary);
          setStatus('paid');
          return;
        }
        if (data?.status === 'failed') {
          setStatus('timeout');
          return;
        }
      } catch {
        /* 網絡抖動繼續輪詢 */
      }
      if (cancelled) return;
      setPollCount((n) => n + 1);
    };
    poll();
    const timer = setInterval(poll, 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [orderNum]);

  // 輪詢 24 次（約 2 分鐘）後轉為 timeout 提示（仍可手動重試）
  React.useEffect(() => {
    if (status === 'polling' && pollCount >= 24) {
      setStatus('timeout');
    }
  }, [status, pollCount]);

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [status]);

  const isPickup = summary?.orderType === 'pickup';

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="max-w-lg w-full text-center">
          {/* ===== 處理中 ===== */}
          {status === 'polling' && (
            <>
              <div className="flex justify-center mb-6">
                <Loader2 size={88} className="text-orange-500 animate-spin" strokeWidth={1.5} />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">{t('order:kpay.confirming')}</h1>
              <p className="text-gray-600 mb-2">
                {t('order:kpay.orderNum')} <span className="font-mono font-bold text-orange-600">{orderNum}</span>
              </p>
              <p className="text-sm text-gray-500">
                {t('order:kpay.pollingHint', { count: pollCount })}
              </p>
            </>
          )}

          {/* ===== 付款成功：訂單完成頁 ===== */}
          {status === 'paid' && (
            <>
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div className="absolute inset-0 bg-green-400/30 rounded-full blur-lg opacity-60"></div>
                  <CheckCircle size={110} className="text-green-600 relative z-10" strokeWidth={1.5} />
                </div>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">{t('order:kpay.success')}</h1>
              <p className="text-gray-600 mb-6">{t('order:kpay.thanks')}</p>

              {summary && (
                <div className="bg-white border-2 border-orange-100 rounded-2xl shadow-sm overflow-hidden mb-6 text-left">
                  {/* 訂單編號 */}
                  <div className="bg-orange-50 px-5 py-4 flex items-center justify-between">
                    <span className="text-sm font-bold text-orange-800">{t('order:kpay.orderNum')}</span>
                    <span className="font-mono font-bold text-orange-600 text-lg">{summary.orderNum}</span>
                  </div>

                  {/* 品項明細 */}
                  {summary.items.length > 0 && (
                    <div className="px-5 py-4 border-b border-gray-100">
                      <p className="flex items-center gap-1.5 text-sm font-bold text-gray-700 mb-3">
                        <ClipboardList size={16} className="text-orange-500" /> {t('order:kpay.details')}
                      </p>
                      <ul className="space-y-2">
                        {summary.items.map((it, idx) => (
                          <li key={idx} className="flex justify-between text-sm text-gray-700">
                            <span>{it.name} × {it.quantity}</span>
                            <span className="font-medium">HK${it.unitPrice * it.quantity}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* 金額 */}
                  <div className="px-5 py-4 space-y-1.5 border-b border-gray-100 text-sm">
                    <div className="flex justify-between text-gray-600">
                      <span>{t('order:kpay.subtotal')}</span><span>HK${summary.subtotal}</span>
                    </div>
                    {summary.voucherDiscount > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>{t('order:kpay.voucherDiscount')}</span><span>−HK${summary.voucherDiscount}</span>
                      </div>
                    )}
                    {summary.deliveryFee > 0 && (
                      <div className="flex justify-between text-gray-600">
                        <span>{t('order:kpay.deliveryFee')}</span><span>HK${summary.deliveryFee}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                      <span className="font-bold text-gray-800">{t('order:kpay.paidTotal')}</span>
                      <span className="text-2xl font-bold text-orange-600">HK${summary.payable}</span>
                    </div>
                  </div>

                  {/* 送貨資訊 */}
                  <div className="px-5 py-4">
                    <p className="flex items-center gap-1.5 text-sm font-bold text-gray-700 mb-2">
                      {isPickup ? <Store size={16} className="text-orange-500" /> : <Truck size={16} className="text-orange-500" />}
                      {isPickup ? t('order:kpay.pickupArrangement') : t('order:kpay.deliveryArrangement')}
                    </p>
                    <div className="text-sm text-gray-600 space-y-1">
                      {isPickup ? (
                        <>
                          <p>{t('order:kpay.pickupLocation', { location: summary.estate || '門市' })}</p>
                          <p>{t('order:kpay.pickupDate', { date: summary.deliveryDate || '待確認' })}</p>
                        </>
                      ) : (
                        <>
                          <p>{t('order:kpay.deliveryEstate', { estate: summary.estate || '—' })}</p>
                          <p>
                            {t('order:kpay.deliveryDate', { date: summary.deliveryDate || '待確認' })}
                            {summary.deliveryTime ? `（${summary.deliveryTime}）` : ''}
                          </p>
                        </>
                      )}
                      <p className="text-xs text-gray-400 pt-1">{t('order:kpay.whatsappConfirmNote')}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => navigate('/')}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-orange-600 text-white font-bold rounded-xl hover:bg-orange-700 transition"
                >
                  <Home size={18} /> {t('order:kpay.backHome')}
                </button>
                <a
                  href="https://wa.me/85257961378"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-orange-200 text-orange-700 font-bold rounded-xl hover:bg-orange-50 transition"
                >
                  <MessageCircle size={18} /> {t('order:kpay.whatsappCs')}
                </a>
              </div>
            </>
          )}

          {/* ===== 逾時 / 錯誤 ===== */}
          {(status === 'timeout' || status === 'error') && (
            <>
              <div className="flex justify-center mb-6">
                <AlertCircle size={100} className="text-amber-500" strokeWidth={1.5} />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">
                {status === 'error' ? t('order:kpay.missingOrder') : t('order:kpay.cannotConfirm')}
              </h1>
              <p className="text-gray-600 mb-2">
                {status === 'error'
                  ? t('order:kpay.invalidLink')
                  : t('order:kpay.paymentSafeNote')}
              </p>
              {orderNum && (
                <p className="text-sm text-gray-500 mb-6">
                  {t('order:kpay.orderNum')} <span className="font-mono font-bold text-orange-600">{orderNum}</span>
                </p>
              )}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {status === 'timeout' && (
                  <button
                    onClick={() => { setStatus('polling'); setPollCount(0); }}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-orange-600 text-white font-bold rounded-xl hover:bg-orange-700 transition"
                  >
                    <Loader2 size={18} /> {t('order:kpay.retry')}
                  </button>
                )}
                <button
                  onClick={() => navigate('/')}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-orange-200 text-orange-700 font-bold rounded-xl hover:bg-orange-50 transition"
                >
                  <Home size={18} /> {t('order:kpay.backHome')}
                </button>
                <a
                  href="https://wa.me/85257961378"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-orange-200 text-orange-700 font-bold rounded-xl hover:bg-orange-50 transition"
                >
                  <MessageCircle size={18} /> {t('order:kpay.whatsappCs')}
                </a>
              </div>
            </>
          )}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
};

export default KpayReturn;
